import { get, chain, toLower, find, toUpper, map } from "lodash"
import { createSelector } from "reselect"
import { VIEW_TYPES } from "helpers/base"
import { getGroup, menuContentSets, MENU_ITEMS_GROUPS } from "helpers/menu"
import { getMostUsedOrDefaultModelId } from "helpers/models"
import { getAllAvailableRollups } from "helpers/rollups"
import { getGrouping } from "helpers/compositeSecurities"
import { unsupportedCurrencyWarning } from "helpers/currency"
import { getEndDate, getPeriodOrStartAndEndDates, getStartDate } from "./dates"
import { getBasePositionSet } from "./base"
import { getSegment } from "./filters"
import { getPortfolios } from "selectors/portfolios"
import { createQueryStringSelector, getRawSelectedModelId } from "selectors/queryString"
import { getIsOpPro, getDefaultCurrencyId } from "modules/account-root/selectors"
import { getMissingDataFiles, betasAreMissing, modelSupportsComposites } from "selectors/missingDataFiles"
import { NORMALIZATION } from "helpers/segments"
import { getDefaultDateRangeProps } from "helpers/shareMyView"
import { getModels } from "resources/models"
import { getModelSupportedCurrencies } from "selectors/modelMetadata"
import { computeAssetTickerMic } from "../helpers"

const EMPTY_ARRAY = []

export const getRawGroupingId = createQueryStringSelector("grouping")
export const getRawGroupingType = createQueryStringSelector("groupingType")
export const getExpand = createQueryStringSelector("expand", false)
export const getSubScreenId = (_, props) => get(props, "params.subScreenId")
export const getShowNotHeld = createQueryStringSelector("showNotHeld", false)
export const getAssetId = (_, props) => get(props, "params.assetId")
export const getAssetType = (_, props) => toLower(get(props, "params.assetType"))
export const getAssetDetails = (state) => get(state, "global.data.assetDetails.data")
export const getClassifications = (state) => get(state, "global.data.classifications.data.classifications")
export const getFilter = createQueryStringSelector("filter")
export const getSelectedScreen = (_, props) => get(props, "path", "assets")
export const getSelectedSubScreen = (_, props) => get(props, "params.selectedSubScreen", "exposure")
export const getNormalization = createQueryStringSelector("normalization", NORMALIZATION.CONTRIBUTION)
export const getIsActive = createQueryStringSelector("isActive", false)
const getFactorCategoryId = createQueryStringSelector("factorCategory")
const getContentSets = (state) => get(state, "global.data.contentSets.data.contentSets")
const getForecasts = state => get(state, "global.data.compositeForecasts.data.forecasts", EMPTY_ARRAY)
const getResourceCurrencyId = state => get(state, "global.data.resourceMeta.data.currencyOverride")

export const getDisplayShowNotHeld = createSelector(
  [getIsActive, getSelectedScreen, getSelectedSubScreen, getFactorCategoryId],
  (isActive, selectedScreen, selectedSubScreen, factorCategory) => {
    const isFactorsView = selectedScreen === "factors"
    const isConcentrationView = selectedScreen === "trends" && selectedSubScreen === "composition" && factorCategory === "concentration"
    return isActive && !isFactorsView && !isConcentrationView
  },
)

const getDefaultModelId = createSelector(
  [getPortfolios, getModels],
  getMostUsedOrDefaultModelId,
)

export const getSelectedModelId = createSelector(
  [getRawSelectedModelId, getDefaultModelId, getModels],
  (selectedModelId, defaultModelId, models) => {
    const modelId = toUpper(selectedModelId)
    if (!selectedModelId || !find(models, model => toUpper(model.id) === modelId)) {
      return defaultModelId
    }
    return modelId
  },
)

export const getSelectedModelName = createSelector(
  [getSelectedModelId, getModels],
  (selectedModelId, models) => chain(models)
    .find(model => model.id === selectedModelId)
    .get("name")
    .value(),
)

export const getInternalId = createSelector(
  [getAssetDetails],
  (asset) => get(asset, "internalId"),
)

const getAccountRollups = createSelector(
  [getClassifications],
  (classifications) => ({ classifications }),
)

const getViewType = createSelector(
  [getIsActive],
  (isActive) => isActive ? VIEW_TYPES.ACTIVE : VIEW_TYPES.ABSOLUTE,
)

export const getFactorSensitivityType = createSelector(
  [getSelectedSubScreen, getSubScreenId],
  (selectedSubScreen, subScreenId) => {
    if (selectedSubScreen === "factor-sensitivity") {
      return toUpper(subScreenId)
    }
    return null
  },
)

export const getSelectedAnalyzeView = createSelector(
  [getSubScreenId, getSelectedSubScreen],
  (subScreenId, selectedSubScreen) => {
    if (subScreenId) {
      if (selectedSubScreen === "factor-sensitivity") {
        return `${subScreenId}-factor-sensitivity`
      }
      return subScreenId
    }
    return selectedSubScreen
  },
)

export const getAvailableRollups = createSelector(
  [getAccountRollups, getEndDate, getViewType],
  getAllAvailableRollups,
)

export const getAssetTickerMic = createSelector(
  [getAssetDetails],
  computeAssetTickerMic,
)

export const getAssetName = createSelector(
  [getAssetDetails],
  (assetDetails) => get(assetDetails, "name", "Composite Asset"),
)

export const getAssetInfoContent = createSelector(
  [getAssetDetails],
  (assetDetails) => {
    if (!assetDetails) { return "" }

    if (assetDetails.indexId) {
      return assetDetails.description
    }

    if (assetDetails.assetClass === "swap") {
      return assetDetails.ticker
    }
  },
)

export const getCurrentGrouping = createSelector(
  [getRawGroupingId, getRawGroupingType, getAvailableRollups, getIsActive],
  getGrouping,
)

const getAnalyzeTypes = createSelector(
  [getIsOpPro, getMissingDataFiles, getSelectedModelId, getContentSets, getForecasts],
  (isOpPro, missingDataFiles, modelId, contentSets, forecasts) => {
    const missingBetas = betasAreMissing(missingDataFiles, modelId)
    return [
      {
        id: "performance",
        name: "Performance",
        base: true,
        tooltip: "Ex-post performance with factor attribution",
      }, {
        id: "risk",
        name: "Predictive Risk",
        base: true,
        disabled: isOpPro,
        tooltip: isOpPro ? "Only available with enterprise plan" : "Ex-ante total risk & factor risk decomposition",
      }, {
        id: "exposure",
        name: "Exposure",
        base: true,
        tooltip: "Weighted-average factor exposure",
      }, {
        id: "composition",
        name: "Composition",
        base: true,
        disabled: missingBetas,
        tooltip: missingBetas ? "There are no Predicted or Historical beta values for the selected model" : "Beta as provided by the selected risk model",
      },
      {
        type: "factorSensitivity",
        id: "predicted-factor-sensitivity",
        name: "Predicted Factor Sensitivity",
        path: "factor-sensitivity/predicted",
        base: true,
        disabled: isOpPro,
        tooltip: isOpPro
          ? "Only available with enterprise plan"
          : "Expected return impact from factors based on factor exposure and expected factor volatility. Factor volatility is estimated using the risk model covariance matrix.",
      },
      {
        type: "factorSensitivity",
        id: "historical-factor-sensitivity",
        name: "Historical Factor Sensitivity",
        path: "factor-sensitivity/historical",
        base: true,
        tooltip: "Expected return impact from factors based on factor exposure and expected factor volatility. Factor volatility is estimated using historical realized factor returns.",
      },
      ...map(forecasts, (forecast) => ({ ...forecast, type: "forecast" })),
      ...menuContentSets(contentSets),
    ]
  })

export const getAnalyzeMenuItems = createSelector(
  [getAnalyzeTypes],
  (analyzeTypes) => map(analyzeTypes, (type, i) => ({
    order: i,
    id: type.id,
    label: type.name,
    ...getGroup(type),
    ...type,
  })),
)

export const getMenuItems = createSelector(
  [getAssetType],
  (assetType) => [
    {
      id: "assets",
      label: "Assets Review",
      tooltip: `Review key analytics for each asset held in this ${assetType === "etf" ? "ETF" : assetType}`,
      order: 0,
      ...MENU_ITEMS_GROUPS.layout,
    },
    {
      id: "trends",
      label: "Factor Trends",
      tooltip: "Factor trends allows factor metrics to be graphed over time",
      order: 1,
      ...MENU_ITEMS_GROUPS.layout,
    },
    {
      id: "factors",
      label: "Factor Review",
      order: 2,
      ...MENU_ITEMS_GROUPS.layout,
    },
  ],
)

export const getSubMenuItems = createSelector(
  [getSelectedScreen, getIsOpPro],
  (screen, isOpPro) => screen === "factors" ? [
    {
      id: "performance",
      label: "Performance",
      order: 0,
    },
    {
      id: "risk",
      label: "Risk",
      order: 1,
      disabled: isOpPro,
      tooltip: isOpPro ? "Only available with enterprise plan" : null,
    },
  ] : EMPTY_ARRAY,
)

export const getModelSupportsComposites = createSelector(
  [getMissingDataFiles, getSelectedModelId],
  modelSupportsComposites,
)

export const getCurrency = createSelector(
  [getDefaultCurrencyId, getResourceCurrencyId],
  (accountCurrencyId, resourceCurrencyId) => resourceCurrencyId || accountCurrencyId,
)

export const getUnsupportedCurrencyWarning = createSelector(
  [getModelSupportedCurrencies, getDefaultCurrencyId, getResourceCurrencyId],
  unsupportedCurrencyWarning,
)

export const getShareMyViewProps = createSelector(
  [getPeriodOrStartAndEndDates, getStartDate, getEndDate],
  getDefaultDateRangeProps,
)

export const getBasePositionSetWithSegment = createSelector(
  [getBasePositionSet, getSegment],
  (basePositionSet, segment) => {
    return {
      ...basePositionSet,
      segment,
    }
  },
)

export const getIsDownloadAllowed = createSelector(
  [getAssetDetails],
  (assetsDetails) => {
    if (!assetsDetails) {
      return false
    }

    return get(assetsDetails, "allowConstituentsDownload", true)
  },
)
